import React, {
  useRef, useEffect, useCallback, useState,
  forwardRef,
  useImperativeHandle
} from 'react'
import { Text } from 'grommet'
import ReCAPTCHA from 'react-google-recaptcha'
import { useResponsiveWrapperContext } from './context'
import { SkeletonLoader } from '@usap/widgets'
import useWmoEnabled from '@usap/carparts/src/shared/hooks/useWmoEnabled'
import useIsInvisibleCaptcha from '../../../carparts/src/shared/hooks/useIsInvisibleCaptcha'
import useRecaptchaLoader from '../../../carparts/src/shared/hooks/useRecaptchaLoader'
import captchConfig from '../../../carparts/src/config/captcha-config'

const Captcha = forwardRef(({
  id,
  name,
  setCaptcha,
  error,
  siteKey,
  reset,
  style = {},
  recaptchaType = '',
  callBackFunc = '',
  GTM,
  isCheckoutSignIn = false,
}, ref) => {
  const { size } = useResponsiveWrapperContext()
  const isWmoEnabled = useWmoEnabled()
  const isInvisibleCaptcha = useIsInvisibleCaptcha()
  const _reCaptchaRef = useRef()
  const { isRecaptchaLoaded, asyncScriptOnLoad, error: captchaError } = useRecaptchaLoader();
  const { siteInvisibleKey } = captchConfig

  const onChangeHandler = useCallback(gRecaptchaResponse => {
    setCaptcha(gRecaptchaResponse)

    // recaptcha GTM events for sign in checkout
    if (isCheckoutSignIn) {
      if (gRecaptchaResponse) {
        GTM.recaptchaBoxCheckedSignInCheckout()
      } else {
        GTM.recaptchaBoxExpiredSignInCheckout()
      }
    }
  }, [setCaptcha, isCheckoutSignIn, GTM])


  useEffect(() => {
    if (__BROWSER__) {
      let _elemRecaptchaResponse = document.getElementById(id)
      _elemRecaptchaResponse =
        (_elemRecaptchaResponse &&
          _elemRecaptchaResponse.querySelector(
            '[name="hidden-g-recaptcha-response"]',
          )) ||
        null

      if (_elemRecaptchaResponse && _elemRecaptchaResponse.value) {
        setCaptcha(_elemRecaptchaResponse.value)
      }
    }
  }, [id, setCaptcha])

  useEffect(
    () => {
      if (__BROWSER__ && reset && _reCaptchaRef?.current) {
        _reCaptchaRef.current.reset()
      }
    },
    [reset],
  )

  useEffect(() => {
    const applyPointerEvents = () => {
      const recaptchaFrame = document.querySelector('iframe[src*="bframe"]');
      if (recaptchaFrame) {
        const recaptchaContainer = recaptchaFrame.parentElement.parentElement;
        if (recaptchaContainer) {
          recaptchaContainer.style.pointerEvents = 'all';
        }
      }
    };

    const observer = new MutationObserver(() => {
      applyPointerEvents();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    applyPointerEvents();

    return () => observer.disconnect();
  }, []);

  // Expose the executeRecaptcha function to the parent
  useImperativeHandle(ref, () => ({
    executeRecaptcha: async () => {
      const token = await _reCaptchaRef.current?.executeAsync();
      return token;
    },
    executeRecaptchaSync: () => {
      return _reCaptchaRef.current?.execute();
    },
    resetCaptcha: () => {
      return _reCaptchaRef.current?.reset();
    }
  }));

  return (
    <>
      {recaptchaType === '' && size === 'small' && !isInvisibleCaptcha ? (
        <style>
          {`#${id} 
            {
              transform: scale(0.85);
              -webkit-transform: scale(0.85);
              transform-origin: 0 0;
              -webkit-transform-origin: 0 0;
            }`}
        </style>
      )
        : ''}
      {!isInvisibleCaptcha && isWmoEnabled ?
        !isRecaptchaLoaded && <SkeletonLoader height="78px" width="100%" backgroundColor="#E9E9EA" />
        : null
      }
      <ReCAPTCHA
        id={id}
        name={name}
        ref={_reCaptchaRef}
        sitekey={isInvisibleCaptcha ? siteInvisibleKey : siteKey}
        onChange={onChangeHandler}
        asyncScriptOnLoad={asyncScriptOnLoad}
        size={isInvisibleCaptcha ? "invisible" : recaptchaType}
        callback={callBackFunc}
        style={{
          display: 'inline-block',
          ...style,
        }}
      />
      <Text style={{ display: 'block' }} color="status-critical" size="xsmall">
        {error || captchaError}
      </Text>
    </>
  )
});

export default Captcha;
